import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { RowActionIndicator } from '@/components/atoms/RowActionIndicator/RowActionIndicator';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { createSelectionColumn } from '@/components/ui/table/selection-column';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import { getStatusSortValue } from '@/modules/collections/utils/statusSorting';
import { stripHttps, truncateUrl } from '@/modules/collections/utils/urlFormatting';
import { CollectionDocument } from '@/shared/types/collections';
import { COLUMN_PRESETS } from '@/util/constants';
import { timeFromNow } from '@/util/dateUtils';

import ActionsCell from './ActionsCell';
import {
  getContentTableIcon,
  getDatasourceStatusText,
  getFlavour,
  getKbRowAction,
} from '../../../helper';
import { ColumnAvatar } from '../ColumnAvatar';
import { FAQAnswer } from '../FAQAnswer';

export const columns = ({
  updateDocument,
  moveDocument,
  createDatasourceAsync,
  category,
  canWrite,
}): ColumnDef<CollectionDocument>[] => [
  createSelectionColumn<CollectionDocument>({
    getIcon: () => getContentTableIcon(category)!,
    getLabel: (row) => row.name,
  }),
  {
    id: 'name',
    accessorKey: 'name',
    header: ({ column }) => (
      <SortableHeader column={column} className="pl-2">
        {t('collections.datasources.datasources')}
      </SortableHeader>
    ),
    cell: ({ row }) => {
      const isFaqType = category === 'faq';
      const isDocumentType = category === 'documents';
      const isExternalType = category === 'external';
      const isWebpagesType = category === 'webpages';
      const action = getKbRowAction(row.original, category);

      return (
        <div className="flex items-center gap-3 pl-2">
          <div className="flex min-w-0 flex-col">
            <span className="block truncate font-medium">{row.original.name}</span>
            {(isFaqType || (isDocumentType && row.original.type === 'txt')) && (
              <FAQAnswer
                collection_id={row.original.collection_id}
                datasource_id={row.original.datasource_id}
                document_id={row.original.document_id}
                preview={row.original.metadata?.preview as string | undefined}
              />
            )}
            {(isExternalType || isWebpagesType) && row.original.external_url && (
              <a
                href={row.original.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block truncate text-foreground-muted hover:underline"
              >
                {stripHttps(truncateUrl(row.original.external_url))}
              </a>
            )}
          </div>
          {action && <RowActionIndicator action={action} />}
        </div>
      );
    },
    ...COLUMN_PRESETS.FLEX,
    meta: { preset: 'FLEX' },
  },
  {
    id: 'status',
    accessorFn: getStatusSortValue,
    header: ({ column }) => <SortableHeader column={column}>{t('common.status')}</SortableHeader>,
    cell: (info) => {
      const { is_draft, status } = info.row.original;
      const getStatusText = () => {
        return getDatasourceStatusText(status, is_draft, t);
      };
      const getTooltipContent = () => {
        if (status === 'available') {
          if (is_draft) {
            return t('collections.status_tooltip.draft');
          }
          return t('collections.status_tooltip.published');
        }
        return null;
      };

      const tooltipContent = getTooltipContent();

      if (!tooltipContent) {
        return (
          <Badge
            flavour={getFlavour(status, is_draft)}
            type="secondary"
            rounded={false}
            className="px-2 font-semibold"
          >
            {getStatusText()}
          </Badge>
        );
      }

      return (
        <InfoTooltip content={tooltipContent} side="top">
          <Badge
            flavour={getFlavour(status, is_draft)}
            type="secondary"
            rounded={false}
            className="px-2 font-semibold"
          >
            {getStatusText()}
          </Badge>
        </InfoTooltip>
      );
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
  },
  {
    id: 'created_by',
    header: () => t('common.created_by'),
    cell: ({ row }) => {
      const createdById = row.original.created_by;

      return (
        <div className="pl-3">
          <ColumnAvatar createdById={createdById} />
        </div>
      );
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
  },
  {
    accessorKey: 'updated',
    header: ({ column }) => <SortableHeader column={column}>{t('common.updated')}</SortableHeader>,
    cell: ({ row }) => {
      const updated = row.original.updated;
      return (
        <div>
          {updated ? (
            <TimestampTooltip timestamp={updated}>{timeFromNow(updated)}</TimestampTooltip>
          ) : (
            '-'
          )}
        </div>
      );
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
  },
  {
    id: 'enable',
    header: () => t('common.active'),
    cell: (info) => {
      const row = info.row.original;

      return (
        <Switch
          checked={row.enabled}
          onCheckedChange={(enabled) => {
            updateDocument({ ...row, enabled });
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          id={`enable-${row.document_id}`}
          disabled={!canWrite}
        />
      );
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
  },
  {
    id: 'actions',
    header: () => '',
    cell: ({ row }) => {
      const item = row.original;
      return (
        <ActionsCell
          item={item}
          moveDocument={moveDocument}
          createDatasourceAsync={createDatasourceAsync}
        />
      );
    },
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' },
  },
];
